import { clsx } from 'clsx';
import type { SkeletonDocumentProps, SkeletonListProps } from './MasterDocument.types';
import { useMasterDocumentTranslation } from './i18n';

// ─── Skeleton Block ────────────────────────────────────────

function SkeletonBlock({ className }: { className?: string }) {
  return (
    <div
      className={clsx(
        'rounded-md bg-gradient-to-r from-background-secondary to-background-secondary',
        'animate-pulse',
        'dark:from-dark-bg-secondary dark:to-dark-bg-primary',
        className,
      )}
    />
  );
}

// ─── SkeletonDocument ──────────────────────────────────────

/**
 * Esqueleto de carga para el documento maestro completo.
 *
 * Replica la estructura visual de ActionBar, ContextStrip, HeaderRenderer,
 * LinesSection y FactBox con bloques animados de gradiente para indicar
 * que el contenido está siendo cargado.
 *
 * @see MasterDocument.types - SkeletonDocumentProps
 */
export function SkeletonDocument({ className }: SkeletonDocumentProps) {
  const { t } = useMasterDocumentTranslation();
  return (
    <div
      className={clsx('flex flex-col gap-4', className)}
      aria-busy="true"
      aria-label={t('master_document.loading.document')}
      role="status"
    >
      {/* ActionBar skeleton */}
      <div className="flex items-center justify-between h-[52px] px-4 bg-bg-primary rounded-t-xl dark:bg-dark-bg-primary">
        <div className="flex items-center gap-3">
          <SkeletonBlock className="w-20 h-5" />
          <div className="w-px h-5 bg-border-secondary dark:bg-dark-border-primary" />
          <SkeletonBlock className="w-28 h-5" />
          <SkeletonBlock className="w-16 h-5 rounded-full" />
        </div>
        <div className="flex items-center gap-2">
          <SkeletonBlock className="w-20 h-8 rounded-md" />
          <SkeletonBlock className="w-20 h-8 rounded-md" />
          <SkeletonBlock className="w-24 h-8 rounded-md" />
        </div>
      </div>

      {/* ContextStrip skeleton */}
      <div className="flex items-center h-12 px-4 bg-bg-primary gap-6 dark:bg-dark-bg-primary">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-1">
            <SkeletonBlock className="w-14 h-2.5" />
            <SkeletonBlock className="w-20 h-3.5" />
          </div>
        ))}
      </div>

      <div className="flex gap-4">
        {/* Main content */}
        <div className="flex-1 flex flex-col gap-4">
          {/* Header section 1 */}
          <div className="bg-bg-primary rounded-2xl p-5 dark:bg-dark-bg-primary">
            <SkeletonBlock className="w-36 h-4 mb-4" />
            <div className="grid grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-1.5">
                  <SkeletonBlock className="w-16 h-2.5" />
                  <SkeletonBlock className="h-9 rounded-md" />
                </div>
              ))}
            </div>
          </div>

          {/* Header section 2 */}
          <div className="bg-bg-primary rounded-2xl p-5 dark:bg-dark-bg-primary">
            <SkeletonBlock className="w-44 h-4 mb-4" />
            <div className="grid grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-1.5">
                  <SkeletonBlock className="w-16 h-2.5" />
                  <SkeletonBlock className="h-9 rounded-md" />
                </div>
              ))}
            </div>
          </div>

          {/* Lines section */}
          <div className="bg-bg-primary rounded-2xl overflow-hidden dark:bg-dark-bg-primary">
            <div className="flex items-center justify-between px-4 py-3 bg-background-secondary dark:bg-dark-bg-secondary">
              <SkeletonBlock className="w-32 h-4" />
              <SkeletonBlock className="w-24 h-8 rounded-md" />
            </div>
            <div className="flex bg-background-secondary px-3 py-2 gap-4 dark:bg-dark-bg-secondary">
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonBlock key={i} className="flex-1 h-3" />
              ))}
            </div>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex px-3 py-3 gap-4 border-b border-border-secondary dark:border-dark-border-primary">
                {Array.from({ length: 5 }).map((_, j) => (
                  <SkeletonBlock key={j} className="flex-1 h-4" />
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* FactBox */}
        <div className="hidden xl:block w-[264px] shrink-0">
          <div className="bg-background-secondary rounded-2xl p-2 flex flex-col gap-2 dark:bg-dark-bg-secondary">
            <div className="bg-bg-primary rounded-xl p-3.5 dark:bg-dark-bg-primary">
              <SkeletonBlock className="w-full h-16 rounded-xl mb-3" />
              <div className="grid grid-cols-2 gap-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <SkeletonBlock key={i} className="h-6" />
                ))}
              </div>
            </div>
            <div className="bg-bg-primary rounded-xl p-3.5 dark:bg-dark-bg-primary">
              <SkeletonBlock className="w-24 h-3 mb-3" />
              {Array.from({ length: 3 }).map((_, i) => (
                <SkeletonBlock key={i} className="h-3 mb-2" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SkeletonList ──────────────────────────────────────────

/**
 * Esqueleto de carga para la vista de lista de documentos.
 *
 * Replica la estructura visual de DocumentList con bloques animados:
 * encabezado, pills de estado, columnas de tabla y filas de datos.
 *
 * @see MasterDocument.types - SkeletonListProps
 */
export function SkeletonList({ rows = 6, className }: SkeletonListProps) {
  const { t } = useMasterDocumentTranslation();
  return (
    <div
      className={clsx(
        'bg-bg-primary rounded-2xl overflow-hidden',
        'shadow-sm',
        'dark:bg-dark-bg-primary dark:shadow-none dark:border dark:border-dark-border-primary',
        className,
      )}
      aria-busy="true"
      aria-label={t('master_document.loading.list')}
      role="status"
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <SkeletonBlock className="w-48 h-6 mb-2" />
            <SkeletonBlock className="w-32 h-3" />
          </div>
          <div className="flex items-center gap-2">
            <SkeletonBlock className="w-24 h-8 rounded-md" />
            <SkeletonBlock className="w-20 h-8 rounded-md" />
          </div>
        </div>
      </div>

      {/* Pills */}
      <div className="flex items-center gap-2 px-5 pb-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <SkeletonBlock key={i} className="w-20 h-7 rounded-full" />
        ))}
      </div>

      {/* Table header */}
      <div className="flex bg-background-secondary px-4 py-2 gap-4 dark:bg-dark-bg-secondary">
        {Array.from({ length: 5 }).map((_, i) => (
          <SkeletonBlock key={i} className="flex-1 h-3" />
        ))}
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex px-4 py-3.5 gap-4 border-b border-border-secondary dark:border-dark-border-primary last:border-0">
          {Array.from({ length: 5 }).map((_, j) => (
            <SkeletonBlock key={j} className="flex-1 h-4" />
          ))}
        </div>
      ))}

      {/* Pagination */}
      <div className="flex items-center justify-between px-5 py-2.5 bg-background-secondary dark:bg-dark-bg-secondary">
        <SkeletonBlock className="w-32 h-3" />
        <div className="flex gap-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonBlock key={i} className="w-7 h-7 rounded-md" />
          ))}
        </div>
      </div>
    </div>
  );
}
